// [SCOPE 051 demo / 053] Authored es-ES bundle.
//
// Demonstrates the localization framework end-to-end: the keys translated here
// render in Spanish when the locale is es-ES; any key NOT present falls back to
// en-US per key (FR-002). Currently scoped to the landing page (the wired slice)
// plus common/settings; the full app-wide extraction is spec 052.
//
// Methodology/lifecycle terms and brand/compliance names (wxKanban, SOC 2, HIPAA,
// GDPR, HITRUST) are intentionally left untranslated.
import type { PartialLocaleBundle } from "./en-US";

export const esES: PartialLocaleBundle = {
  common: {
    save: "Guardar",
    cancel: "Cancelar",
    close: "Cerrar",
    delete: "Eliminar",
    edit: "Editar",
    loading: "Cargando…",
    saving: "Guardando…",
    saved: "Guardado",
    error: "Se ha producido un error",
    retry: "Reintentar",
    yes: "Sí",
    no: "No",
  },
  settings: {
    language: {
      title: "Idioma",
      description: "Elige el idioma de la interfaz de la aplicación.",
      help: "Los idiomas sin traducir vuelven al inglés hasta que se redactan sus textos.",
      saved: "Idioma actualizado.",
    },
  },
  landing: {
    nav: {
      home: "Inicio",
      features: "Funcionalidades",
      pricing: "Precios",
      docs: "Ayuda/Tutoriales",
      signIn: "Iniciar sesión",
    },
    hero: {
      subtitle: "Sistema de gestión de proyectos con IA",
      description:
        "wxKanban es un sistema integral de gestión de proyectos desarrollado en torno a la IA, con un kit de inicio listo para producción para equipos de cualquier tamaño. Diseñado para la seguridad, el cumplimiento normativo y unas integraciones fluidas.",
      getStarted: "Empezar",
      learnMore: "Más información",
    },
    install: {
      eyebrow: "Empieza en ~30 minutos",
      heading: "Instalación y configuración",
      description:
        "¿Nuevo en wxKanban? Nuestra guía paso a paso te lleva de un ordenador nuevo a una configuración funcional en VS Code: instalar Claude Code, añadir el Dev Cockpit y poner en marcha tu primer proyecto. No se necesita experiencia previa.",
      step1: {
        title: "Instalar las herramientas",
        body: "VS Code, Node.js y Claude Code, con comandos para copiar y pegar.",
      },
      step2: {
        title: "Añadir el Dev Cockpit",
        body: "Instala la extensión y conéctala a tu proyecto de wxKanban.",
      },
      step3: {
        title: "Ejecutar tu primer flujo de trabajo",
        body: "Scope, spec y build, guiado a través del ciclo de vida de 6 fases.",
      },
      cta: "Ver las instrucciones de configuración",
    },
    features: {
      heading: "Funcionalidades potentes",
      subheading:
        "Todo lo que necesitas para gestionar proyectos, registrar el tiempo y colaborar con tu equipo",
      aiPowered: {
        title: "Impulsado por IA",
        description:
          "Sugerencias de tareas inteligentes y optimización del flujo de trabajo, impulsadas por tu herramienta de IA favorita para el desarrollo",
      },
      security: {
        title: "Seguridad empresarial",
        description: "Conforme con SOC 2, HIPAA y RGPD, con cifrado AES-256-GCM",
      },
      integration: {
        title: "Integración multisistema",
        description:
          "Conecta Jira, Monday.com, Asana, Trello y otras plataformas de gestión de proyectos sin complicaciones",
      },
      conversion: {
        title: "Conversión de aplicaciones heredadas",
        intro: "Convierte tus aplicaciones heredadas como:",
        more: "y más",
      },
      collaboration: {
        title: "Colaboración en equipo",
        item1:
          "Gestión de equipos en tiempo real con control de acceso basado en roles",
        item2:
          "Añade los documentos SDLC de tu empresa para analizarlos en busca de conformidad",
        item3:
          "Asigna el SDLC a cada miembro del equipo para cumplir con los controles de auditoría",
        item4:
          "Deja que la IA te ayude a cumplir con todos los requisitos de auditoría",
      },
      lifecycle: {
        title: "Ciclo de vida de 6 fases",
        item1: "Metodología de calidad estructurada, de Design a Release",
        item2:
          "Comandos de código abierto y Claude para gestionar todo el proceso de desarrollo",
        item3:
          "Actualiza automáticamente las specs y las tareas en tu sistema de gestión de proyectos",
        item4: "Flujos automáticos para garantizar la conformidad con las auditorías",
        item5: "Informe de auditoría completo en cada versión",
        footer:
          "Lo que escribes y generas en VS Code, ahora en un completo sistema de gestión de proyectos",
      },
    },
    pricing: {
      heading: "Precios sencillos y transparentes",
      subheading:
        "Elige el plan perfecto para tu equipo. Siempre con la flexibilidad de subir o bajar de nivel",
      mostPopular: "MÁS POPULAR",
      perMonth: "/mes",
      free: "Gratis",
      faqText: "¿Tienes preguntas sobre los precios?",
      contactSales: "Contacta con nuestro equipo de ventas",
      tiers: {
        single: {
          name: "Usuario individual",
          userLimit: "1 usuario",
          f1: "Proyectos ilimitados",
          f2: "Una integración de gestión de proyectos",
          f3: "Soporte por correo electrónico",
          cta: "Empezar ahora",
        },
        smb: {
          name: "Pequeña/Mediana empresa",
          userLimit: "Hasta 5 usuarios",
          f1: "Certificado SOC 2 y HIPAA",
          f2: "Una sola empresa",
          f3: "Gestión de equipos",
          f4: "Soporte por correo electrónico",
          cta: "Empezar ahora",
        },
        consultant: {
          name: "Consultor",
          userLimit: "Equipo ilimitado",
          f1: "Miembros de equipo ilimitados",
          f2: "Varias conexiones de gestión de proyectos",
          f3: "Seguimiento del tiempo avanzado",
          f4: "Generación de facturas",
          f5: "Flujos de trabajo personalizados",
          f6: "Acceso a la API",
          cta: "Registrarse",
        },
        enterprise: {
          name: "Empresa",
          priceUnit: "+ 5 $/usuario por encima de 25",
          userLimit: "25 o más usuarios",
          f1: "25 usuarios incluidos",
          f2: "5 $/mes por usuario activo por encima de 25",
          f3: "Todo ilimitado",
          f4: "Soporte dedicado",
          f5: "Integraciones personalizadas",
          f6: "Garantía de SLA",
          cta: "Empezar ahora",
        },
      },
    },
    compliance: {
      heading: "Seguridad de nivel empresarial",
      subheading:
        "Diseñado con la conformidad y la seguridad como requisitos fundamentales",
      soc2: {
        name: "Alineado con SOC 2",
        description: "Diseñado según los Trust Services Criteria de SOC 2",
      },
      hipaa: {
        name: "Controles orientados a HIPAA",
        description:
          "Salvaguardas basadas en las normas de información sanitaria protegida",
      },
      gdpr: {
        name: "Preparado para el RGPD",
        description: "Controles de privacidad y protección de datos integrados",
      },
      encryption: {
        title: "Cifrado en reposo y en tránsito",
        description: "Cifrado AES-256-GCM para todos los datos",
      },
      auth: {
        title: "Autenticación segura",
        description:
          "Hash con bcrypt con tokens JWT y rotación de tokens de actualización",
      },
      audit: {
        title: "Registro de auditoría",
        description: "Rastro de auditoría completo para la conformidad y la transparencia",
      },
      rbac: {
        title: "Control de acceso basado en roles",
        description: "Permisos detallados para equipos y datos",
      },
      trustLead: "Diseñado para la confianza",
      trustBody:
        "La seguridad de tus datos es nuestra máxima prioridad. Nos guiamos por estándares de seguridad reconocidos y revisamos nuestros controles con regularidad.",
    },
    cta: {
      headline: "¿Listo para transformar tu gestión de proyectos?",
      subhead: "Únete a miles de equipos que utilizan wxKanban para agilizar sus flujos de trabajo",
      getStarted: "Empezar",
      viewPricing: "Ver precios",
    },
    footer: {
      tagline: "Gestión de proyectos impulsada por IA para equipos modernos",
      product: "Producto",
      company: "Empresa",
      legal: "Aspectos legales",
      features: "Funcionalidades",
      pricing: "Precios",
      documentation: "Documentación",
      about: "Acerca de",
      blog: "Blog",
      contact: "Contacto",
      bio: "Biografía",
      privacy: "Privacidad",
      terms: "Condiciones",
      security: "Seguridad",
      rights: "© 2026 wxKanban. Todos los derechos reservados.",
    },
  },
  login: {
    title: "Te damos la bienvenida de nuevo",
    subtitle: "Inicia sesión en tu cuenta de wxKanban",
    email: "Correo electrónico",
    emailPlaceholder: "tu@empresa.com",
    password: "Contraseña",
    rememberMe: "Recordarme",
    forgotPassword: "¿Has olvidado tu contraseña?",
    signIn: "Iniciar sesión",
    signingIn: "Iniciando sesión…",
    genericError: "Se ha producido un error inesperado. Inténtalo de nuevo.",
    noAccount: "¿No tienes una cuenta?",
    register: "Registra tu empresa",
    tagline: "Gestión de tareas multiplataforma",
    emailLabel: "Correo electrónico",
    showPasswordAria: "Mantén pulsado para mostrar la contraseña",
    registerLink: "Regístrate aquí",
    connectPmPrompt: "O conecta un sistema de gestión de proyectos para empezar:",
    loginFailed: "Error al iniciar sesión",
  },
  forgotPassword: {
    title: "Contraseña olvidada",
    description: "Introduce tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.",
    successMessage: "Si existe una cuenta con ese correo electrónico, se ha enviado un enlace para restablecer la contraseña. Revisa tu bandeja de entrada.",
    backToSignIn: "Volver a iniciar sesión",
    emailLabel: "Correo electrónico",
    emailPlaceholder: "tu@ejemplo.com",
    submit: "Enviar enlace de restablecimiento",
    submitting: "Enviando...",
    errorFailed: "No se pudo enviar el enlace de restablecimiento",
  },
  resetPassword: {
    title: "Restablecer contraseña",
    subtitle: "Introduce tu nueva contraseña a continuación.",
    newPassword: "Nueva contraseña",
    confirmNewPassword: "Confirmar nueva contraseña",
    updatePassword: "Actualizar contraseña",
    updating: "Actualizando...",
    successMessage: "Tu contraseña se ha restablecido correctamente. Redirigiendo al inicio de sesión...",
    backToSignIn: "Volver a iniciar sesión",
    errMissingLink: "Este enlace para restablecer la contraseña no es válido o está incompleto.",
    errMissingToken: "Falta el token de restablecimiento. Utiliza el enlace de tu correo electrónico.",
    errPasswordMin: "La contraseña debe tener al menos 8 caracteres",
    errPasswordMismatch: "Las contraseñas no coinciden",
    errFailed: "No se pudo restablecer la contraseña",
  },
  changePassword: {
    title: "Cambia tu contraseña",
    welcome: "¡Bienvenido, {{name}}! Por seguridad, cambia tu contraseña temporal antes de continuar.",
    currentLabel: "Contraseña actual (temporal)",
    currentPlaceholder: "Introduce tu contraseña temporal",
    newLabel: "Nueva contraseña",
    newPlaceholder: "Crea una nueva contraseña (mín. 8 caracteres)",
    confirmLabel: "Confirmar nueva contraseña",
    confirmPlaceholder: "Confirma tu nueva contraseña",
    submit: "Cambiar contraseña y continuar",
    needHelp: "¿Necesitas ayuda? Contacta con tu administrador o con el",
    supportTeam: "equipo de soporte",
    errMinLength: "La nueva contraseña debe tener al menos 8 caracteres",
    errMismatch: "Las contraseñas no coinciden",
    errSameAsOld: "La nueva contraseña debe ser diferente de la actual",
    errFailed: "No se pudo cambiar la contraseña",
    successRedirect: "¡Contraseña cambiada correctamente! Redirigiendo al panel...",
  },
  appnav: {
    dashboard: "Panel",
    teamMembers: "Miembros del equipo",
    projects: "Proyectos",
    pmIntegrations: "Integraciones PM",
    myTasks: "Mis tareas",
    workHub: "Centro de trabajo",
    timeBilling: "Tiempo y facturación",
    invoices: "Facturas",
    reports: "Informes",
    settings: "Configuración",
    gettingStarted: "Primeros pasos",
    manual: "Manual", // [SCOPE 141 / T016]
    help: "Ayuda",
    signOut: "Cerrar sesión",
  },
  register: {
    next: "Siguiente",
    loading: "Cargando...",
    back: "Atrás",
    companyTitle: "Información de la empresa",
    companySubtitle: "Cuéntanos sobre tu empresa para que podamos configurar tu facturación y comunicación",
    companyNameLabel: "Nombre de la empresa *",
    billingEmailLabel: "Correo de facturación *",
    billingAddressLabel: "Dirección de facturación *",
    countryLabel: "País *",
    phoneLabel: "Número de teléfono *",
    referredByLabel: "Recomendado por (opcional)",
    referredByHint: "Si un distribuidor te recomendó, introduce su código.",
    errCompanyNameRequired: "El nombre de la empresa es obligatorio",
    errCompanyNameMin: "El nombre de la empresa debe tener al menos 2 caracteres",
    errCompanyNameMax: "El nombre de la empresa debe tener menos de 255 caracteres",
    errBillingEmailRequired: "El correo de facturación es obligatorio",
    errEmailInvalid: "Introduce una dirección de correo válida",
    errBillingAddressRequired: "La dirección de facturación es obligatoria",
    errBillingAddressMin: "La dirección de facturación debe tener al menos 10 caracteres",
    errBillingAddressMax: "La dirección de facturación debe tener menos de 500 caracteres",
    errCountryRequired: "El país es obligatorio",
    errCountryInvalid: "Selecciona un código de país válido",
    errPhoneRequired: "El número de teléfono es obligatorio",
    errPhoneMin: "El número de teléfono debe tener al menos 10 caracteres",
    errPhoneMax: "El número de teléfono debe tener menos de 20 caracteres",
    adminTitle: "Crear cuenta de administrador",
    adminSubtitle: "Configura tu cuenta de administrador para acceder a wxKanban. Es el primer usuario que se creará para tu empresa.",
    emailLabel: "Dirección de correo *",
    firstNameLabel: "Nombre *",
    lastNameLabel: "Apellido (opcional)",
    passwordLabel: "Contraseña *",
    pwReqTitle: "Requisitos de la contraseña:",
    pwReqLength: "Al menos 12 caracteres",
    pwReqUpper: "Al menos una letra mayúscula",
    pwReqLower: "Al menos una letra minúscula",
    pwReqNumber: "Al menos un número",
    pwReqSpecial: "Al menos un carácter especial (!@#$%^&*)",
    termsPrefix: "Al crear una cuenta, aceptas nuestros",
    termsOfService: "Términos de servicio",
    and: "y",
    privacyPolicy: "Política de privacidad",
    creatingAccount: "Creando cuenta...",
    completeRegistration: "Completar registro",
    errEmailRequired: "El correo es obligatorio",
    errPasswordRequired: "La contraseña es obligatoria",
    errPasswordRequirements: "La contraseña no cumple todos los requisitos",
    errFirstNameRequired: "El nombre es obligatorio",
    errFirstNameLength: "El nombre debe tener entre 1 y 100 caracteres",
    errLastNameLength: "El apellido debe tener entre 1 y 100 caracteres",
    paymentMethod: "Método de pago",
    preparingPayment: "Preparando el pago seguro…",
    initPaymentError: "No se pudo inicializar el pago",
    subscribingTo: "Te estás suscribiendo a",
    at: "por",
    perMonth: "/mes",
    stripeNotice: "Los datos de la tarjeta los gestiona directamente Stripe. Ningún dato sensible pasa por nuestros servidores.",
    paymentFailedCard: "El pago falló. Comprueba tu tarjeta e inténtalo de nuevo.",
    paymentFailed: "El pago falló",
    processing: "Procesando…",
    completePayment: "Completar pago",
    startPromo: "Iniciar periodo gratuito",
    inviteChecking: "Comprobando tu invitación…",
    inviteInvalid: "Este enlace de invitación no es válido. Puede que ya se haya usado, se haya revocado o haya caducado.",
    inviteFreeMonths: "Te han invitado: tus primeros {{days}} días en {{plan}} son gratis.",
    inviteBoundTo: "Esta invitación es para {{email}} — te registrarás con esa dirección.",
    promoFreeForDays: "Tus primeros {{days}} días son gratis.",
    promoFirstChargeOn: "Tu primer pago de {{amount}} será el {{date}}.",
    promoFirstChargeAfter: "Tu primer pago de {{amount}} será después de {{days}} días.",
    promoCardNotChargedToday: "Hoy no se hará ningún cargo a tu tarjeta. La guardamos para que tu suscripción continúe automáticamente.",
    tierSingleUser: "Usuario único",
    tierSmallBusiness: "Pequeña/Mediana empresa",
    tierConsultant: "Consultor",
    tierEnterprise: "Empresa",
    tierFree: "Gratis",
    confTitle: "Confirma tu registro",
    confSubtitle: "Revisa tu información antes de completar el registro",
    confCompanyDesc: "Detalles sobre tu organización",
    confCompanyName: "Nombre de la empresa",
    confBillingEmail: "Correo de facturación",
    subscriptionPlan: "Plan de suscripción",
    subscriptionPlanDesc: "Tu nivel y precio seleccionados",
    selectedPlan: "Plan seleccionado",
    monthlyPrice: "Precio mensual",
    adminAccount: "Cuenta de administrador",
    adminAccountDesc: "Tus datos de inicio de sesión de administrador",
    nameLabel: "Nombre",
    emailShort: "Correo",
    termsClickPrefix: "Al hacer clic en «Completar registro», aceptas nuestros",
    title: "Bienvenido a wxKanban",
    settingUp: "Configurando tu cuenta",
    account: "",
    stepCompany: "Empresa",
    stepPayment: "Pago",
    stepAdmin: "Admin",
    successTitle: "¡Empresa creada correctamente!",
    verificationSent: "Se ha enviado un correo de verificación a",
    whatsNext: "Próximos pasos:",
    next1: "Revisa tu correo y haz clic en el enlace de verificación",
    next2: "Inicia sesión en tu nueva cuenta de wxKanban",
    next3: "Completa la configuración de tu equipo y empieza a gestionar proyectos",
    goToLogin: "Ir a iniciar sesión",
    createAnother: "Crear otra cuenta",
    haveAccount: "¿Ya tienes una cuenta?",
    signIn: "Iniciar sesión",
  },
  settingsPage: {
    title: "Configuración",
    subtitle: "Administra la configuración de tu empresa y cuenta",
    tabCompany: "Empresa",
    tabProfile: "Perfil",
    tabNotifications: "Notificaciones",
    tabSecurity: "Seguridad",
    companyInfo: "Información de la empresa",
    companyInfoDesc: "Actualiza los datos y la información de facturación de tu empresa",
    profileInfo: "Información del perfil",
    profileInfoDesc: "Actualiza tu información personal y tus preferencias",
    language: "Idioma",
    changePassword: "Cambiar contraseña",
    notificationPrefs: "Preferencias de notificación",
    securitySettings: "Configuración de seguridad",
    saveChanges: "Guardar cambios",
    saveProfile: "Guardar perfil",
  },
  dashboard: {
    failedToFetch: "Error al obtener: {{status}}",
    failedToLoadPendingTasks: "Error al cargar las tareas pendientes",
    adminTitle: "Panel de administración",
    lastUpdated: "Última actualización: {{time}}",
    welcome: "Bienvenido, {{email}}",
    panelCompany: "Empresa",
    totalUsers: "Usuarios totales",
    activeUsers: "Usuarios activos",
    byRole: "Por rol:",
    panelProjects: "Proyectos",
    totalProjects: "Proyectos totales",
    active: "Activos",
    panelTeamSummary: "Resumen del equipo",
    total: "Total",
    panelFinancials: "Finanzas",
    revenue: "Ingresos (este mes)",
    invoices: "Facturas",
    outstanding: "Pendientes",
    panelActivity: "Actividad",
    loadingActivities: "Cargando actividades…",
    noActivity: "Sin actividad reciente",
    panelPendingTasks: "Tareas pendientes",
    loading: "Cargando…",
    noActiveProjects: "No hay proyectos activos.",
    colProject: "Proyecto",
    colOpen: "Abiertas",
    colInProgress: "En curso",
  },
  projects: {
    title: "Proyectos",
    noProjects: "Aún no hay proyectos",
    count: "{{count}} proyectos",
    countOne: "1 proyecto",
    showAll: "Mostrar todos",
    myProjects: "Mis proyectos",
    newProject: "Nuevo proyecto",
    searchPlaceholder: "Buscar proyectos…",
    sort: "Ordenar:",
    sortUpdated: "Actualizado",
    sortCreated: "Creado",
    sortName: "Nombre",
    loadError: "No se pudieron cargar los proyectos",
    noMatch: "Ningún proyecto coincide con los filtros actuales",
    clearFilters: "Borrar filtros",
    createFirst: "Crea tu primer proyecto",
  },
  tasks: {
    back: "Volver al panel",
    title: "Mis tareas",
    lastSync: "Última sincronización:",
    syncNow: "Sincronizar",
    syncing: "Sincronizando…",
    allSources: "Todas las fuentes",
    allPriorities: "Todas las prioridades",
    priorityCritical: "Crítica",
    priorityHigh: "Alta",
    priorityMedium: "Media",
    priorityLow: "Baja",
    allStatuses: "Todos los estados",
    statusOpen: "Abierta",
    statusInProgress: "En curso",
    statusCompleted: "Completada",
    statusOrphaned: "Huérfana",
    sortUpdated: "Actualizadas recientemente",
    sortDue: "Fecha de vencimiento",
    sortPriority: "Prioridad",
    sortTitle: "Título",
    loading: "Cargando tareas…",
    noTasks: "Aún no hay tareas",
    connectHint1: "Conecta un sistema PM desde",
    integrations: "Integraciones",
    connectHint2: "y haz clic en Sincronizar para importar tus tareas asignadas.",
  },
  integrations: {
    title: "Integraciones de sistemas de gestión de proyectos",
    subtitle: "Conecta tus herramientas de gestión de proyectos a wxKanban",
    viewTasks: "Ver mis tareas →",
    connectedSuccess: "✓ ¡Sistema de gestión de proyectos conectado correctamente!",
    loading: "Cargando integraciones...",
    connectedSince: "Conectado desde el {{date}}",
    notConnected: "No conectado",
    active: "✓ Activo",
    disconnect: "Desconectar",
    connect: "Conectar",
    needHelp: "¿Necesitas ayuda para conectar?",
    helpText: "Cada sistema de gestión de proyectos requiere credenciales OAuth. Consulta la documentación para ver las instrucciones de configuración.",
    confirmDisconnect: "¿Desconectar {{system}}?",
  },
  invoices: {
    title: "Facturas",
    subtitle: "Genera y gestiona facturas de clientes a partir de los registros de tiempo facturables",
    generate: "Generar factura",
    statTotal: "Total de facturas",
    statRevenue: "Ingresos totales",
    statSent: "Facturas enviadas",
    modalTitle: "Generar nueva factura",
    modalDesc: "Selecciona el rango de fechas para los registros de tiempo facturables",
    startDate: "Fecha de inicio",
    endDate: "Fecha de fin",
    generateConfirm: "Generar",
    history: "Historial de facturas",
    emptyTitle: "Aún no se han generado facturas",
    emptyHint: "Crea tu primera factura para empezar",
    colNumber: "N.º de factura",
    colDate: "Fecha",
    colBilledTo: "Facturado a",
    colHours: "Horas",
    colAmount: "Importe",
    colStatus: "Estado",
    colActions: "Acciones",
    statusSent: "Enviada",
    statusDraft: "Borrador",
    na: "N/D",
    detailTitle: "Factura {{number}}",
    generatedOn: "Generada el {{date}}",
    hoursWorked: "Horas trabajadas",
    totalAmount: "Importe total",
    downloadPdf: "Descargar PDF",
    sendInvoice: "Enviar factura",
    resend: "Reenviar",
  },
  reports: {
    title: "Informes",
    subtitle: "Consulta análisis detallados sobre el seguimiento del tiempo, los proyectos y el rendimiento del equipo",
    tabTime: "Informes de tiempo",
    tabProjects: "Informes de proyectos",
    dateRange: "Rango de fechas",
    exportCsv: "Exportar CSV",
    timeByMember: "Tiempo por miembro del equipo",
    timeByMemberDesc: "Desglose de las horas registradas por cada miembro del equipo",
    selectDateRange: "Selecciona un rango de fechas para ver los informes de tiempo",
    colMember: "Miembro del equipo",
    colTotalHours: "Horas totales",
    colBillableHours: "Horas facturables",
    colBillablePct: "% facturable",
    projectPerformance: "Rendimiento de proyectos",
    projectPerformanceDesc: "Resumen de todos los proyectos y su progreso",
    noProjects: "No se encontraron proyectos",
    colProject: "Proyecto",
    colHours: "Horas",
    colTasks: "Tareas",
    colCompletion: "Finalización",
    colTeamSize: "Tamaño del equipo",
  },
  workhub: {
    summaryTotal: "Tareas totales",
    summaryPastDue: "Vencidas",
    summaryAging: "Más de 30 días",
    changeStatus: "Cambiar estado de la tarea",
    task: "Tarea",
    newStatus: "Nuevo estado",
    blockedDesc: "una dependencia externa impide el progreso.",
    suspendedDesc: "en pausa para reevaluación.",
    reason: "Motivo",
    reasonPlaceholder: "Explica por qué se pausa esta tarea...",
    reasonRequired: "Se requiere un motivo para las tareas bloqueadas o suspendidas.",
    updateStateError: "No se pudo actualizar el estado de la tarea",
    unknownError: "Error desconocido",
    submit: "Enviar",
    saving: "Guardando...",
    cancel: "Cancelar",
    saveTimeEntry: "Guardar registro de tiempo",
    duration: "Duración",
    billableQ: "¿Facturable?",
    billable: "Facturable",
    nonBillable: "No facturable",
    notesOptional: "Notas (opcional)",
    notesPlaceholder: "¿En qué trabajaste?",
    saveEntry: "Guardar registro",
    saveEntryError: "No se pudo guardar el registro",
    taskActivity: "Actividad de la tarea",
    loadingActivity: "Cargando la actividad de la tarea...",
    loadDetailError: "No se pudieron cargar los detalles de la tarea",
    timeEntries: "Registros de tiempo ({{count}})",
    noTimeEntries: "Aún no hay registros de tiempo.",
    invoiced: "Facturado",
    statusHistory: "Historial de estados ({{count}})",
    noStateChanges: "No se registraron cambios de estado.",
    pmSyncState: "Estado de sincronización PM",
    syncStatus: "Estado:",
    syncUnknown: "desconocido",
    lastSyncLabel: "Última sincronización: {{time}}",
    retries: "Reintentos: {{count}}",
    errorLabel: "Error: {{message}}",
    resolved: "Resuelto: {{time}}",
    aiBadge: "IA",
    manualBadge: "Manual",
    running: "en curso",
    title: "Centro de trabajo del consultor",
    subtitle: "Cola de tareas unificada con filtrado por proyecto y seguimiento del tiempo con un clic.",
    allProjects: "Todos los proyectos",
    timerRunning: "Temporizador en marcha",
    stopTimer: "Detener temporizador",
    inactivityTitle: "El temporizador lleva más de 8 horas en marcha",
    inactivityBody: "¿Sigue siendo trabajo activo? Detén el temporizador si te ausentaste.",
    keepRunning: "Mantener en marcha",
    loadingQueue: "Cargando la cola del consultor...",
    loadError: "No se pudieron cargar los datos del centro de trabajo.",
    assignedQueue: "Cola de trabajo asignada",
    noTasks: "No se encontraron tareas asignadas para este filtro.",
    compliance: "Cumplimiento",
    activity: "Actividad",
    blocked: "Bloqueada",
    suspended: "Suspendida",
    stop: "Detener",
    startTimer: "Iniciar temporizador",
    activeTask: "Tarea activa",
    billablePreview: "Vista previa facturable lista para facturar",
    complianceFlaggedTasks: "Tareas marcadas por cumplimiento: {{count}}",
    loadingPreview: "Cargando vista previa facturable...",
    previewError: "No se pudo cargar la vista previa facturable.",
    previewSummary: "Entradas: {{count}} • Total de horas facturables: {{hours}}",
    sourceBreakdown: "Desglose por origen:",
    manual: "Manual:",
    ai: "IA:",
    complianceReviewPanel: "Panel de revisión de cumplimiento",
    loadingFlags: "Cargando marcas de cumplimiento...",
    flagsError: "No se pudieron cargar las marcas de cumplimiento.",
    noFlags: "No hay marcas de cumplimiento abiertas.",
  },
  billing: {
    title: "Configuración de facturación",
    subtitle: "Configura las tarifas por hora para el seguimiento del tiempo y la facturación. Las tarifas siguen una jerarquía: Usuario → Proyecto → Predeterminada de la empresa.",
    fetchError: "No se pudieron obtener las tarifas de facturación",
    updateError: "No se pudo actualizar la tarifa",
    updateSuccess: "Tarifa de facturación actualizada correctamente",
    invalidRate: "Introduce una tarifa válida",
    resolutionTitle: "Orden de resolución de tarifas:",
    resolutionBody: "Al calcular los importes facturables, el sistema comprueba las tarifas en este orden: 1) tarifa específica del usuario, 2) tarifa específica del proyecto, 3) tarifa predeterminada de la empresa.",
    companyRate: "Tarifa predeterminada de la empresa",
    companyRateDesc: "Tarifa de reserva utilizada cuando no hay tarifa de usuario o proyecto",
    hourlyRate: "Tarifa por hora (USD)",
    projectRates: "Tarifas específicas de proyecto",
    projectRatesDesc: "Anular la predeterminada de la empresa para proyectos específicos",
    projectIdLabel: "ID de proyecto: {{id}}",
    userRates: "Tarifas específicas de usuario",
    userRatesDesc: "Anular todas las demás tarifas para miembros del equipo específicos",
    noTeamMembers: "No se encontraron miembros del equipo",
    customRate: "Tarifa personalizada",
    usingDefault: "Usando predeterminada",
    defaultPlaceholder: "Predeterminada",
  },
  verification: {
    noToken: "No se proporcionó ningún token de verificación. Por favor, compruebe el enlace de su correo electrónico.",
    verifiedSuccess: "¡Su correo electrónico se ha verificado correctamente!",
    verifyFailed: "No se pudo verificar el correo electrónico. Es posible que el enlace haya caducado.",
    unexpectedError: "Se produjo un error inesperado. Vuelva a intentarlo o póngase en contacto con el soporte.",
    resendFailed: "No se pudo reenviar el correo de verificación. Vuelva a intentarlo.",
    networkError: "Error de red. Vuelva a intentarlo.",
    titleLoading: "Verificando su correo electrónico...",
    titleSuccess: "¡Correo electrónico verificado!",
    titleError: "Verificación fallida",
    descLoading: "Espere mientras verificamos su dirección de correo electrónico",
    descSuccess: "Ahora puede iniciar sesión en su cuenta",
    descError: "Encontramos un problema con su verificación",
    signInNow: "Iniciar sesión ahora",
    resending: "Reenviando...",
    resendButton: "Reenviar correo de verificación",
    resentNotice: "¡Correo de verificación reenviado! Revise su bandeja de entrada.",
    backToSignIn: "Volver a iniciar sesión",
    redirecting: "Redirigiendo al inicio de sesión en 3 segundos...",
  },
  invitePage: {
    errorNoToken: "No se proporcionó ningún token de invitación",
    errorInvalidExpired: "Invitación no válida o caducada",
    errorPasswordMismatch: "Las contraseñas no coinciden",
    errorPasswordTooShort: "La contraseña debe tener al menos 8 caracteres",
    errorAcceptFailed: "No se pudo aceptar la invitación",
    errorUnexpected: "Se produjo un error inesperado",
    invalidTitle: "Invitación no válida",
    invalidDescription: "Este enlace de invitación no es válido o ha caducado.",
    invalidContactPrompt: "Póngase en contacto con la persona que le invitó para solicitar una nueva invitación.",
    goToSignIn: "Ir a Iniciar sesión",
    acceptedTitle: "¡Bienvenido al equipo!",
    acceptedDescription: "Su cuenta se ha creado correctamente.",
    acceptedBody: "Ahora puede iniciar sesión para acceder al espacio de trabajo de wxKanban de su empresa.",
    signInNow: "Iniciar sesión ahora",
    joinTitle: "Unirse a {{companyName}}",
    invitedBy: "{{inviterName}} le ha invitado a unirse como {{role}}",
    emailLabel: "Correo electrónico",
    firstNameLabel: "Nombre",
    lastNameLabel: "Apellido",
    passwordLabel: "Contraseña",
    confirmPasswordLabel: "Confirmar contraseña",
    creatingAccount: "Creando cuenta...",
    acceptAndCreate: "Aceptar invitación y crear cuenta",
    alreadyHaveAccount: "¿Ya tiene una cuenta?",
    signInHere: "Inicie sesión aquí",
  },
  inviteAccept: {
    subtitle: "Invitación al equipo",
    validating: "Validando invitación...",
    expiredTitle: "Invitación caducada",
    expiredBody: "Este enlace de invitación ha caducado. Pide a tu administrador que lo reenvíe.",
    usedTitle: "Invitación ya utilizada",
    invalidTitle: "Invitación no válida",
    usedBody: "Esta invitación ya ha sido aceptada. Intenta iniciar sesión.",
    invalidBody: "Este enlace de invitación no es válido o ha caducado.",
    goToLogin: "Ir a iniciar sesión",
    successTitle: "¡Cuenta creada!",
    successBody: "Tu cuenta se ha configurado. Ahora puedes iniciar sesión en wxKanban.",
    logIn: "Iniciar sesión",
    joinCompany: "Unirse a {{company}}",
    creatingAccountFor: "Creando cuenta para",
    fullNameLabel: "Nombre completo",
    passwordLabel: "Contraseña",
    passwordPlaceholder: "Mín. 8 caracteres",
    confirmPasswordLabel: "Confirmar contraseña",
    confirmPasswordPlaceholder: "Vuelve a introducir la contraseña",
    timezoneLabel: "Zona horaria",
    timezoneHint: "Detectada automáticamente desde tu navegador",
    creatingAccount: "Creando cuenta...",
    createAccount: "Crear cuenta",
    alreadyHaveAccount: "¿Ya tienes una cuenta?",
    signIn: "Iniciar sesión",
    errorPasswordMismatch: "Las contraseñas no coinciden",
    errorPasswordTooShort: "La contraseña debe tener al menos 8 caracteres",
    errorCreateAccountFailed: "No se pudo crear la cuenta",
    errorUnknown: "Error desconocido",
  },
  inviteTeam: {
    title: "Invitar a miembros del equipo",
    subtitle: "Agrega colegas para colaborar en tus proyectos",
    sendInvitations: "Enviar invitaciones",
    sendInvitationsDesc: "Elige cómo quieres invitar a los miembros de tu equipo",
    singleInvite: "Invitación individual",
    bulkUpload: "Carga masiva",
    sendMore: "Enviar más invitaciones",
    viewTeam: "Ver equipo",
    tipsTitle: "Consejos para invitar a miembros del equipo",
    tipEmails: "Asegúrate de usar las direcciones de correo electrónico correctas para evitar rebotes",
    tipRoles: "Elige los roles adecuados: Administrador (acceso total), Gerente (puede gestionar proyectos) o Miembro (puede ver y completar tareas)",
    tipCsv: "Para cargas masivas, usa un archivo CSV con las columnas: email, usertype, firstName, lastName",
    tipExpiry: "Las invitaciones caducan después de 7 días si no se aceptan",
  },
  teamDashboard: {
    title: "Mi panel",
    welcomeBack: "Bienvenido de nuevo, {{email}}",
    assignedProjects: "Proyectos asignados",
    openTasks: "Tareas abiertas",
    hoursThisWeek: "Horas esta semana",
    noProjectsAssigned: "Todavía no tienes proyectos asignados.",
    workHubTitle: "Centro de trabajo del consultor",
    workHubDescription: "Abre tu cola de tareas unificada y registra el tiempo directamente en el trabajo asignado.",
    openWorkHub: "Abrir centro de trabajo",
  },
  distributorSignup: {
    errorPasswordsMismatch: "Las contraseñas no coinciden",
    errorPasswordTooShort: "La contraseña debe tener al menos 8 caracteres",
    errorAcceptTerms: "Por favor, acepte los términos de distribuidor",
    errorRegistrationFailed: "Error en el registro",
    errorUnknown: "Error desconocido",
    successHeading: "Todo listo",
    successSignedInPrefix: "Su cuenta gratuita de distribuidor está lista. Ha iniciado sesión como",
    referralCodeLabel: "Su código de referido",
    copyCodeTitle: "Copiar código",
    referralCodeHelp: "Comparta este código con las personas que refiera. Cuando lo introduzcan al registrarse, su cuenta se le atribuirá a usted.",
    goToDashboard: "Ir a su panel",
    introBannerBefore: "Conviértase en distribuidor de wxKanban. Configure su cuenta",
    introBannerFree: "gratuita",
    introBannerAfter: "a continuación: obtendrá un código de referido para compartir y se iniciará su sesión automáticamente al terminar.",
    fieldBusinessName: "Nombre de la empresa *",
    fieldFullName: "Su nombre completo *",
    fieldEmail: "Correo electrónico *",
    fieldPassword: "Contraseña *",
    placeholderPassword: "Al menos 8 caracteres",
    fieldConfirmPassword: "Confirmar contraseña *",
    placeholderConfirmPassword: "Vuelva a introducir la contraseña",
    acceptTermsLabel: "Acepto los términos de distribuidor. Mi cuenta es una cuenta gratuita de un solo usuario, no estoy suscrito a un plan de pago.",
    creatingAccount: "Creando cuenta…",
    createAccountButton: "Crear cuenta gratuita",
    pageSubtitle: "Registro de distribuidor",
  },
  overviewPage: {
    loadFailed: "No se pudo cargar el documento de resumen ({{status}})",
    unknownError: "Error desconocido",
    dashboard: "Panel",
    projectOverview: "Resumen del proyecto",
    refresh: "Actualizar",
    loadErrorTitle: "No se pudo cargar el documento de resumen",
  },
  projectWorkspace: {
    // [SCOPE 137 / T019] Change Impact mode (owner-set)
    impactModeTitle: "Modo de impacto de cambios",
    impactModeHelp: "Cómo trata la comprobación de impacto de cambios del kit las ediciones de IA en este proyecto. Se aplica al kit de cada desarrollador y prevalece sobre un valor local.",
    impactModeNotify: "Notificar: indicar al desarrollador a qué afecta una edición, sin preguntar nunca",
    impactModeBoundaries: "Límites: preguntar solo cuando una edición alcanza otro alcance o una prueba aprobada (predeterminado)",
    impactModeAlways: "Siempre: preguntar cuando una edición afecta a cualquier otra cosa",
    impactModeNotSet: "No se ha definido aquí. El kit de cada desarrollador usa su valor local, o Límites.",
    impactModeSetBy: "Definido por {{who}} el {{when}}",
    impactModeOwnerOnly: "Solo el propietario del proyecto o un administrador de la empresa puede cambiarlo.",
    impactModeDataLoss: "La posible pérdida de datos se detiene en todos los modos.",
    impactModeError: "El cambio no se guardó. Inténtalo de nuevo.",
    complianceTabSettings: "Configuración",
    complianceTabDocuments: "Documentos",
    complianceTabTasks: "Tareas",
    complianceTabAudit: "Auditoría",
    complianceTabReports: "Informes",
    learningsGenerateFailed: "Error ({{status}})",
    learningsPrintHint: "Usa la impresión del navegador para guardar como PDF",
    regenerate: "Regenerar",
    learningsIframeTitle: "Aprendizajes del proyecto",
    learningsNoneTitle: "Aún no hay informe de aprendizajes",
    learningsRunPrefix: "Ejecute",
    learningsRunSuffix: "después de completar el trabajo para capturar decisiones, correcciones y controles de cumplimiento.",
    learningsThenGenerate: "Luego genere el informe a continuación.",
    generating: "Generando…",
    generateLearningsReport: "Generar informe de aprendizajes",
    errFailedLoadProject: "No se pudo cargar el proyecto",
    confirmArchive: "¿Archivar este proyecto? Se ocultará de la lista de proyectos.",
    confirmDelete: "¿Eliminar permanentemente \"{{name}}\"? Esto elimina el proyecto y todas sus tareas, fases, specs y documentos. No se puede deshacer.",
    thisProject: "este proyecto",
    errFailedDeleteProjectStatus: "No se pudo eliminar el proyecto ({{status}})",
    errFailedDeleteProject: "No se pudo eliminar el proyecto",
    errFailedUpdateProject: "No se pudo actualizar el proyecto",
    errFailedUpdateAuditType: "No se pudo actualizar el tipo de auditoría",
    loadingProject: "Cargando proyecto...",
    projectNotFound: "Proyecto no encontrado",
    backToProjects: "Volver a proyectos",
    projects: "Proyectos",
    clientEngagementTitle: "Acceso mediante colaboración activa con {{name}}",
    client: "Cliente",
    clientProjectBadge: "Proyecto de cliente — {{name}}",
    editProject: "Editar proyecto",
    auditLabel: "Auditoría:",
    noAudit: "Sin auditoría",
    archiveProject: "Archivar proyecto",
    archive: "Archivar",
    deleteProjectTitle: "Eliminar proyecto permanentemente",
    delete: "Eliminar",
    tabLifecycle: "Ciclo de vida",
    tabDevelopmentPlan: "Plan de desarrollo",
    tabPortfolioAnalyzer: "Analizador de portafolio",
    tabCompliance: "Cumplimiento",
    tabLearnings: "Aprendizajes",
    tabKit: "Kit",
    views: "Vistas",
    viewMatrix: "Matriz",
    viewStack: "Stack",
    viewTheme: "Tema",
    viewBugReports: "Informes de errores",
    iframeLifecycle: "Ciclo de vida del proyecto",
    iframeDevplan: "Plan de desarrollo",
    phaseNotStarted: "Esta fase aún no ha comenzado.",
    saveFailed: "Error al guardar",
    saveErrorIntro: "Se produjo el siguiente error al guardar:",
    copyToClipboard: "Copiar al portapapeles",
    copy: "Copiar",
    copyHint: "Haz clic en \"Copiar\" para copiar este mensaje de error al portapapeles y reportarlo.",
    close: "Cerrar",
    copyAndClose: "Copiar y cerrar",
    fieldProjectName: "Nombre del proyecto",
    fieldDescription: "Descripción",
    fieldHourlyRate: "Tarifa por hora ($)",
    fieldAllocatedHours: "Horas asignadas",
    fieldPmSystems: "Sistemas PM",
    fieldSecurityClassification: "Clasificación de seguridad",
    fieldAuditType: "Tipo de auditoría",
    phaseAssignments: "Asignaciones de fase",
    loadingTeamMembers: "Cargando miembros del equipo...",
    unassigned: "Sin asignar",
    reassignPermissionNote: "Solo ADMIN y TEAM_MANAGER pueden reasignar fases.",
    cancel: "Cancelar",
    saving: "Guardando...",
    saveChanges: "Guardar cambios",
  },
  helpPage: {
    backToHelpCenter: "Volver al centro de ayuda",
    examples: "Ejemplos",
    wasArticleHelpful: "¿Te resultó útil este artículo?",
    yesItHelped: "Sí, me ayudó",
    noNotHelpful: "No, no fue útil",
    helpCenter: "Centro de ayuda",
    // [SCOPE 138 / T011] "Learn wxKanban with your AI"
    teachTitle: "Aprende wxKanban con tu IA",
    teachHowTo: "Descarga las lecciones, entrega start-here.md (o el zip completo) a cualquier chat de IA y di «enséñame». Cada lección te guía paso a paso por una tarea.",
    teachDownload: "Descargar las lecciones",
    teachVersion: "wxKanban {{version}} · {{count}} lecciones",
    teachError: "La descarga falló. Inténtalo de nuevo.",
    subtitle: "Encuentra respuestas, documentación y guías para wxKanban",
    searchPlaceholder: "Busca artículos de ayuda, comandos CLI o guías...",
    searchResults: "Resultados de búsqueda",
    searching: "Buscando...",
    noResults: "No se encontraron resultados para \"{{query}}\"",
    viewsCount: "{{count}} visualizaciones",
    topicsCount: "{{count}} temas",
    moreCount: "+{{count}} más",
  },
  planHelp: {
    title: "Ayuda",
    subtitlePrefix: "Qué hace cada área de wxKanban y qué incluye tu plan. Se muestra",
    subtitleSuffix: "de forma predeterminada: elige otro plan para comparar.",
    yourPlan: "tu plan",
    included: "Incluido",
    upgradeRequired: "Requiere mejora de plan",
    loading: "Cargando tu plan…",
  },
  faqPage: {
    title: "Ayuda y preguntas frecuentes",
    subtitle: "Explore las respuestas o haga una pregunta y la añadiremos a las preguntas frecuentes.",
    panel: {
      heading: "Preguntas frecuentes",
      loading: "Cargando…",
      empty: "Aún no hay preguntas frecuentes — haga una pregunta abajo.",
      askPrompt: "¿No encontró su respuesta? Pregúntenos",
      placeholder: "Escriba su pregunta…",
      submit: "Enviar pregunta",
      sending: "Enviando…",
      tooShort: "Introduzca una pregunta (al menos 5 caracteres).",
      submitError: "No se pudo enviar su pregunta.",
      thanks: "Gracias — le responderemos.",
    },
  },
  aboutPage: {
    title: "Acerca de wxKanban",
    lede: "wxKanban es una forma de crear software con IA basada en especificaciones: te enseña a trabajar como lo hacen los profesionales, llevando una aplicación a lo largo de todo el ciclo de vida, desde una idea caótica hasta un resultado limpio, probado y entregado, sobre unas bases que realmente te pertenecen.",
    whatItIsHeading: "Qué es",
    whatItIsPara1: "wxKanban es un entorno de desarrollo basado en especificaciones. Avanzas por un ciclo de vida guiado de seis fases —Diseño → Implementación → QA → Pruebas humanas → Beta → Lanzamiento— en el que nada se construye sin una especificación aprobada, y cada cambio es trazable hasta la especificación que lo autorizó. Igual de importante, mantiene tus especificaciones y documentación coherentes a medida que el software evoluciona: los cambios futuros se contrastan con lo ya escrito, de modo que tu documentación se actualiza con el trabajo en lugar de quedarse obsoleta.",
    whatItIsIntro: "En torno a ese ciclo de vida, wxKanban te da lo que necesitas para llevar a cabo trabajo real en equipo:",
    teamMgmtLabel: "Gestión de equipos",
    teamMgmtBody: "— invita a personas, define sus roles y controla quién puede hacer qué.",
    checkoutLabel: "Check-out y check-in",
    checkoutBody: "— reclama un ámbito para trabajar en él y devuélvelo cuando termines, para que el trabajo siga coordinado y nadie sobrescriba en silencio lo de otra persona.",
    cockpitLabel: "El Cockpit",
    cockpitBody: "— un centro de mando en vivo para tu proyecto: en qué punto está, qué sigue y cada comando de wxKanban en un solo lugar.",
    helpLabel: "Ayuda activa y contextual",
    helpBody: "— orientación y respuestas justo donde trabajas, tanto dentro de wxKanban como dentro de VS Code, donde realmente se construye.",
    whatItIsClosing: "Ese es el corazón de wxKanban: el ciclo de vida basado en especificaciones, con las herramientas para llevarlo a cabo en equipo.",
    whyHeading: "Por qué existe",
    whyPara1: "La IA ha cambiado cómo se crea el software, pero la parte difícil nunca fue la sintaxis: fue el ",
    whyEmphasis: "criterio",
    whyPara2: " para llevar una aplicación hasta su entrega. wxKanban está construido para dar a los desarrolladores experimentados una forma de llevar ese criterio a la era de la IA, y para dar a los recién llegados esa disciplina desde el primer día, de modo que la IA acelere tu trabajo en lugar de descontrolarlo.",
    groundHeading: "Construido sobre un terreno que te pertenece",
    groundPara: "Tus datos residen en una base de datos SQL estándar, tu autenticación es propia y puedes cambiar de proveedor de IA cuando quieras. Sin dependencia propietaria, sin un proveedor interponiéndose entre tú y el software del que dependes.",
    behindHeading: "Quién está detrás",
    behindPara1: "wxKanban está hecho por ",
    behindPara2: ", fundada por Andy «Cowboy» Stapleton, que ha pasado más de 40 años atravesando cada gran cambio de plataforma, del mainframe a la web y a la nube, y ayudando a otros desarrolladores a hacer la misma travesía.",
    contactHeading: "Ponte en contacto",
    contactIntro: "¿Preguntas o ideas? Escribe a",
    contactOutro: "— de verdad respondemos.",
    phoneLabel: "Teléfono:",
  },
  bioPage: {
    title: "Sobre el fundador",
    photoAlt: "Retrato del fundador",
    loading: "Cargando…",
  },
  privacyPage: {
    title: "Política de privacidad",
    lastUpdated: "Última actualización: {{date}}",
    intro:
      "wxKanban (“nosotros”, “wxperts”) respeta tu privacidad. Esta política explica qué recopilamos cuando usas la aplicación wxKanban, por qué lo hacemos y las opciones que tienes. Recopilamos lo que necesitamos para prestar el servicio, no vendemos tus datos y puedes pedirnos que los eliminemos.",
    authoritativeNote:
      "Esta política se ha traducido para tu comodidad. La versión en inglés es la versión autorizada.",
    sections: {
      s1heading: "Información que recopilamos",
      s1item1:
        "Datos de cuenta y empresa — tu nombre, dirección de correo electrónico, contraseña (almacenada únicamente como un hash seguro), nombre de la empresa, rol y preferencia de idioma.",
      s1item2:
        "El trabajo que creas — proyectos, tareas, especificaciones, notas, registros de tiempo, facturas y contenido relacionado que introduces en la aplicación.",
      s1item3:
        "Información de facturación — los datos de suscripción y pago son procesados por nuestro proveedor de pagos, Stripe. No almacenamos los números completos de las tarjetas; conservamos tu plan, estado e historial de facturación.",
      s1item4:
        "Servicios conectados — si vinculas una herramienta de gestión de proyectos (por ejemplo, Jira, Monday.com, Asana, Trello), almacenamos los tokens de acceso necesarios para la sincronización, cifrados en reposo.",
      s1item5:
        "Uso y registros — registros técnicos básicos (como acciones, marcas de tiempo y errores) que utilizamos para operar, proteger y mejorar el servicio.",
      s2heading: "Cómo la usamos",
      s2item1: "Para proporcionar y operar la aplicación — tu cuenta, proyectos, seguimiento del tiempo y facturación.",
      s2item2: "Para procesar suscripciones y pagos a través de Stripe.",
      s2item3: "Para sincronizar con las herramientas de gestión de proyectos que elijas conectar.",
      s2item4: "Para impulsar las funciones asistidas por IA (ver más abajo).",
      s2item5: "Para proteger el servicio, prevenir el abuso y ofrecer soporte.",
      s3heading: "Procesamiento con IA",
      s3body:
        "Algunas funciones envían el contenido que proporcionas (por ejemplo, especificaciones o texto relacionado) a proveedores de IA externos —como Google Gemini, OpenAI u otros que configuremos— únicamente para generar el resultado solicitado. No usamos tu contenido para entrenar nuestros propios modelos, y los proveedores de IA lo procesan según sus propios términos como proveedores de servicios nuestros.",
      s4heading: "Con quién la compartimos",
      s4body:
        "No vendemos ni alquilamos tu información. La compartimos únicamente con los proveedores de servicios que nos ayudan a operar wxKanban —por ejemplo, alojamiento en la nube (Amazon Web Services), procesamiento de pagos (Stripe) y los proveedores de IA mencionados arriba— y solo para que puedan prestarnos esos servicios. Podemos divulgar información si así lo exige la ley.",
      s5heading: "Seguridad",
      s5body:
        "Protegemos tus datos con cifrado en tránsito y en reposo, contraseñas con hash y controles de acceso. Ningún sistema es perfectamente seguro, pero trabajamos continuamente para salvaguardar tu información.",
      s6heading: "Cuánto tiempo la conservamos",
      s6body:
        "Conservamos tu cuenta y el contenido que creas mientras tu cuenta esté activa, y durante un período razonable posterior según sea necesario para fines legales, contables y operativos. Puedes pedirnos que eliminemos tus datos en cualquier momento.",
      s7heading: "Tus opciones y derechos",
      s7body:
        "Puedes acceder, corregir, exportar o eliminar tu información personal, y retirar tu consentimiento cuando corresponda. Si te encuentras en una región con derechos específicos de protección de datos (como la UE o el Reino Unido bajo el GDPR, o las leyes de privacidad estatales de EE. UU.), esos derechos te asisten. Para ejercerlos, escribe a",
      s8heading: "Transferencias internacionales",
      s8body:
        "wxKanban se opera desde los Estados Unidos, y tu información puede procesarse allí. Al usar el servicio, consientes esa transferencia, sujeta a las protecciones descritas en esta política.",
      s9heading: "Contacto",
      s9body: "¿Preguntas sobre privacidad? Escribe a",
    },
  },
  termsPage: {
    title: "Términos de uso",
    lastUpdated: "Última actualización: {{date}}",
    intro:
      "Estos términos rigen tu uso de la aplicación y el sitio web de wxKanban. Al crear una cuenta o usar el servicio, los aceptas. Por favor, lee también nuestra",
    authoritativeNote:
      "Estos términos se han traducido para tu comodidad. La versión en inglés es la versión autorizada.",
    sections: {
      s1heading: "Tu cuenta",
      s1body:
        "Eres responsable de la exactitud de los datos de tu cuenta y de mantener tus credenciales seguras. Eres responsable de la actividad que ocurra bajo tu cuenta y de los miembros del equipo que invites.",
      s2heading: "Uso aceptable",
      s2body:
        "Usa wxKanban de forma legal. No intentes interrumpir ni obtener acceso no autorizado al servicio, hacer un uso indebido de los datos de otros usuarios, ni utilizar la plataforma para crear o almacenar contenido ilícito.",
      s3heading: "Suscripciones y facturación",
      s3body:
        "Los planes de pago se facturan a través de Stripe de forma recurrente hasta su cancelación. Las tarifas se cobran por adelantado en cada período de facturación y no son reembolsables, salvo cuando la ley lo exija. Puedes cancelar en cualquier momento; tu plan permanece activo hasta el final del período en curso. Avisaremos con antelación razonable de los cambios de precio.",
      s4heading: "Tu contenido y propiedad",
      s4body:
        "Eres propietario de los datos y el contenido que creas en wxKanban. Nos concedes la licencia limitada necesaria para alojarlo, procesarlo, respaldarlo y mostrarlo, de modo que podamos prestar el servicio. Puedes exportar o eliminar tu contenido tal como se describe en nuestra Política de privacidad.",
      s5heading: "Resultados generados por IA",
      s5body:
        "wxKanban utiliza IA para asistir tu trabajo. Los resultados de la IA pueden ser imperfectos: eres responsable de revisar, probar y aprobar todo lo generado antes de confiar en ello. No garantizamos que los resultados asistidos por IA sean precisos, completos o adecuados para un propósito concreto.",
      s6heading: "Nuestra propiedad intelectual",
      s6body:
        "La aplicación wxKanban, su diseño y los nombres wxKanban y wxperts pertenecen a wxperts o a sus licenciantes. Estos términos no te otorgan ningún derecho sobre nuestro software ni nuestra marca más allá de usar el servicio según lo previsto.",
      s7heading: "Sin garantías",
      s7body:
        "El servicio se proporciona “tal cual” y “según disponibilidad”, sin garantías de ningún tipo. Trabajamos para mantener wxKanban fiable y disponible, pero no garantizamos un funcionamiento ininterrumpido ni libre de errores.",
      s8heading: "Limitación de responsabilidad",
      s8body:
        "En la máxima medida permitida por la ley, wxperts no se hace responsable de daños indirectos, incidentales o consecuentes, ni de la pérdida de datos o beneficios, derivados de tu uso —o de la imposibilidad de usar— el servicio.",
      s9heading: "Terminación",
      s9body:
        "Puedes cerrar tu cuenta en cualquier momento. Podemos suspender o cancelar el acceso si se incumplen estos términos o para proteger el servicio. Tras la terminación, seguirán aplicándose las disposiciones que, por su naturaleza, deban subsistir.",
      s10heading: "Ley aplicable",
      s10body:
        "wxperts opera desde los Estados Unidos, y estos términos se rigen por la ley de EE. UU., sin tener en cuenta los principios de conflicto de leyes. Si usas el servicio desde la Unión Europea, nada en estos términos elimina los derechos obligatorios de protección del consumidor y de los datos que tienes bajo la ley de la UE (incluido el GDPR); esas protecciones siguen aplicándose a ti.",
      s11heading: "Cambios",
      s11body:
        "Podemos actualizar estos términos a medida que el producto evoluciona. La fecha de “última actualización” que aparece arriba indica cuándo cambiaron por última vez; el uso continuado tras un cambio significa que lo aceptas.",
      s12heading: "Contacto",
      s12body: "¿Preguntas sobre estos términos? Escribe a",
    },
  },
  adminDashboard: {
    saveFailed: "Error al guardar",
    editAuditType: "Editar tipo de auditoría",
    complianceFramework: "Marco de cumplimiento",
    none: "— Ninguno —",
    title: "Panel de administración",
    welcomeBack: "Bienvenido de nuevo, {{email}}",
    teamMembers: "Miembros del equipo",
    activeProjects: "Proyectos activos",
    hoursThisWeek: "Horas esta semana",
    timeBilling: "Tiempo y facturación",
    timeBillingDesc: "Registra el tiempo, define las tarifas de facturación y gestiona las horas facturables en todos los proyectos.",
    timeBillingPlaceholder: "Las entradas de tiempo y las tarifas de facturación aparecerán aquí.",
    systemHealth: "Estado del sistema",
    reportsAnalytics: "Informes y análisis",
    reportsDesc: "Resúmenes de ingresos, horas por proyecto, rentabilidad e informes exportables.",
    reports: "Informes",
    reportsPlaceholder: "Los informes financieros aparecerán aquí una vez que se registren las entradas de tiempo.",
  },
  subscriptionCard: {
    statusActive: "Activo",
    statusPending: "Pendiente",
    statusSuspended: "Suspendido",
    currentPlan: "Plan actual",
    teamSeats: "Puestos del equipo",
    seatLimitReached: "Límite de puestos alcanzado — mejora tu plan para invitar a más miembros.",
    upgradePlan: "Mejorar plan",
  },
  engagementsWidget: {
    title: "Colaboraciones",
    clientFirms: "{{count}} consultora en tus proyectos",
    clientFirmsPlural: "{{count}} consultoras en tus proyectos",
    clientsServed: "{{count}} cliente al que atiendes",
    clientsServedPlural: "{{count}} clientes a los que atiendes",
    viewAll: "Ver todo",
    roleHiring: "Contratando",
    roleServing: "Prestando servicio",
    moreCount: "+ {{count}} más",
  },
  activityFeed: {
    title: "Feed de actividad",
    loading: "Cargando actividad reciente...",
    subtitle: "Actividad reciente del equipo y los proyectos",
    viewAll: "Ver todo",
    empty: "Sin actividad reciente",
    emptyHint: "La actividad aparecerá aquí a medida que su equipo trabaje",
    byUser: "por {{name}}",
    justNow: "justo ahora",
    minutesAgo: "hace {{count}} min",
    hoursAgo: "hace {{count}} h",
    daysAgo: "hace {{count}} d",
  },
  dailyDigest: {
    title: "Resumen diario",
    loadingActivity: "Cargando la actividad de hoy...",
    subtitle: "Resumen de la actividad del equipo de hoy",
    configure: "Configurar",
    viewAll: "Ver todo",
    noActivity: "Aún no hay actividad hoy",
    activityWillAppear: "La actividad del equipo aparecerá aquí",
    byUser: "por {{name}}",
    moreItems: "+{{count}} elementos más",
    justNow: "ahora mismo",
    minutesAgo: "hace {{count}} min",
    hoursAgo: "hace {{count}} h",
  },
  roleLock: {
    contactAdmin: "Póngase en contacto con su administrador para acceder a esta sección.",
    requiresAdmin: "Requiere administrador",
  },
  upgradeLock: {
    requiresPlan: "Requiere el plan {{plan}}",
    upgradePlan: "Mejorar el plan",
  },
  projectProposals: {
    errorFindDraft: "No se pudo encontrar el borrador de resumen para esta propuesta",
    errorOpenEditor: "Error al abrir el editor",
    errorFetch: "Error al obtener las propuestas: {{status}}",
    errorLoad: "Error al cargar las propuestas",
    errorUpdateStatus: "Error al actualizar el estado",
    rejectionReasonPrompt: "Motivo del rechazo (obligatorio):",
    title: "Propuestas de proyecto",
    addProposal: "Agregar propuesta",
    loadingProposals: "Cargando propuestas...",
    empty: "Aún no hay propuestas. Agregue una para comenzar.",
    status_pending: "Pendiente",
    status_good_idea: "Buena idea",
    status_accepted: "Aceptada",
    status_rejected: "Rechazada",
    editTitle: "Abrir el editor de resumen para esta propuesta",
    goodIdea: "Buena idea",
    reject: "Rechazar",
    accept: "Aceptar",
    createProject: "Crear proyecto",
    reopen: "Reabrir",
    projectCreated: "Proyecto creado",
  },
  systemHealth: {
    title: "Estado del sistema",
    checked: "Comprobado {{time}}",
    refresh: "Actualizar",
    runningDiagnostics: "Ejecutando diagnósticos…",
    allSystemsOperational: "Todos los sistemas operativos",
    driftOrMissingDetected: "Se detectó desviación de esquema o tablas faltantes",
    databaseUnreachable: "Base de datos inaccesible",
    database: "Base de datos",
    status: "Estado",
    latency: "Latencia",
    errorLabel: "Error",
    tableRows: "filas {{table}}",
    server: "Servidor",
    uptime: "Tiempo activo",
    memory: "Memoria",
    env: "Entorno",
    diagTime: "Tiempo de diag",
    pmIntegrationsConnected: "integraciones PM conectadas",
    pmIntegrationConnected: "integración PM conectada",
    schemaDriftTables: "Desviación de esquema — {{count}} tablas",
    schemaDriftTable: "Desviación de esquema — {{count}} tabla",
    missing: "faltante",
    run: "Ejecute",
    toFix: "para corregir.",
    missingTables: "Tablas faltantes ({{count}})",
    toCreateMissingTables: "para crear las tablas faltantes.",
  },
  projectGrid: {
    opening: "Abriendo…",
    lastUpdated: "Última actualización: {{date}}",
    notCreated: "no creado",
    noAudit: "Sin auditoría",
    editAuditType: "Editar tipo de auditoría",
    outstandingTasks: "Tareas pendientes",
    complete: "Completado",
    inProgress: "En curso",
    taskProgress: "{{total}} tareas - {{completed}} completadas",
    notStarted: "No iniciado",
    failedOpenOverview: "No se pudo abrir el editor de resumen",
    failedOpenOverviewStatus: "No se pudo abrir el editor de resumen ({{status}})",
    titleProjectOverview: "Resumen del proyecto",
    titleProjectLifecycle: "Lifecycle del proyecto",
    titleDevelopmentPlan: "Plan de desarrollo",
    titleAuditReport: "Informe de auditoría",
    titleCompoundLearnings: "Aprendizajes acumulados",
    titleProjectScopeAnalysis: "Análisis de Scope del proyecto",
    titleEntityDiagram: "Diagrama de entidades",
    showDocuments: "Mostrar documentos",
    hideDocuments: "Ocultar documentos",
    newProject: "Nuevo",
    allProjects: "Todos los proyectos",
    active: "Activos",
    branched: "Ramificados",
    emptyAdmin: "Aún no hay proyectos activos. Haz clic en Nuevo para crear tu primer proyecto.",
    emptyTeam: "Aún no tienes proyectos asignados.",
  },
  phaseTabBar: {
    assignedUser: "Usuario asignado",
  },
  designPhase: {
    specifications: "Especificaciones",
    clarifiedCount: "{{clarified}} / {{total}} aclaradas",
  },
  implementationPhase: {
    tasksHeading: "Tareas",
    openCount: "{{n}} abierta(s)",
  },
  regression: {
    loading: "Comparando con ejecuciones anteriores…",
    failed: "No se pudo crear la comparación",
    title: "Comprobación de regresión para {{version}}",
    blocking: "{{count}} bloqueando",
    newlyFailing: "Empezó a fallar",
    newlyFailingHint: "Pasó la última vez que se ejecutó. Esto detiene la publicación.",
    neverPassed: "Nunca pasó",
    neverPassedHint: "Trabajo pendiente, no una regresión.",
    stillPassing: "Sigue pasando",
    lastPassed: "Pasó por última vez en {{version}}",
  },
  testSuite: {
    skipTitle: "Requisito de pruebas",
    skipOn: "Las pruebas están DESACTIVADAS para este proyecto.",
    skipOff: "Las pruebas son obligatorias en este proyecto.",
    skipTurnOn: "Omitir pruebas",
    skipReEnable: "Volver a exigir pruebas",
    skipCancel: "Cancelar",
    skipConfirm: "Esto desactiva la puerta de publicación en TODOS los alcances de este proyecto. Las pruebas obligatorias dejarán de bloquear y las publicaciones se marcarán como omitidas, no como probadas. Queda registrado a su nombre.",
    skipConfirmYes: "Sí, omitir pruebas",
    skipHint: "Solo administradores. El cambio se registra en el log de auditoría.",
    skipBannerTitle: "Las pruebas están desactivadas en este proyecto",
    skipBannerBody: "Las pruebas obligatorias no bloquean las publicaciones. Las puertas informan «omitida», no «superada».",
    scopeFilter: "Alcance:",
    allScopes: "Todos los alcances ({{count}})",
    loading: "Cargando el conjunto de pruebas…",
    loadFailed: "No se pudo cargar el conjunto de pruebas",
    runFailed: "No se pudo iniciar la ejecución",
    empty: "Aún no hay pruebas. Se generan al implementar el alcance.",
    cycle: "Versión {{version}}",
    cycleStatus: { open: "En desarrollo", uat: "En UAT", production: "En producción", superseded: "Reemplazada" },
    humanPending: "{{count}} prueba(s) de usuario esperando la liberación a UAT",
    runGate: "Ejecutar {{gate}}",
    noItemsForGate: "No hay pruebas en la puerta {{gate}}",
    runRunning: "Ejecución en curso",
    runStalled: "Ejecución detenida — sin respuesta del ejecutor",
    runTotals: "{{passed}} correctas · {{failed}} fallidas · {{total}} en total",
    required: "Obligatoria",
    origin: { ai: "IA", user: "Tester" },
    inherit: "Hereda", override: "Anular",
    forceInherited: "Hereda la obligatoriedad del alcance",
    forceOverridden: "Anula la obligatoriedad del alcance",
    forceScopeTitle: "Prueba obligatoria",
    forceScopeLabel: "Exigir aprobación de las {{count}} pruebas de este alcance",
    forceScopeHint: "Si está activo, el producto no puede avanzar hacia producción sin aprobar estas pruebas.",
    filter: { all: "Todas", outstanding: "Pendientes", forced: "Obligatorias", failing: "Fallidas" },
  },
  userTest: {
    loading: "Cargando sus pruebas…",
    loadFailed: "No se pudieron cargar sus pruebas",
    signoffFailed: "No se pudo registrar",
    dismissFailed: "No se pudo descartar la nueva prueba",
    notReleasedTitle: "Todavía no está listo",
    notReleasedBody: "{{count}} prueba(s) en espera. Se liberan cuando las pruebas automáticas de esta versión pasan.",
    summary: "{{required}} obligatorias · {{optional}} opcionales · versión {{version}}",
    required: "Obligatoria", optional: "Opcional",
    testerAuthored: "Añadida por un tester",
    addTest: "Añadir una prueba",
    preconditions: "Antes de empezar", steps: "Pasos", postconditions: "Comprobar después",
    action: "Haga esto", expected: "Debería ver",
    pass: "Correcta", fail: "Fallida",
    notesPlaceholder: "¿Qué ocurrió? (obligatorio si falló)",
    failNeedsReason: "Diga qué ocurrió realmente — se convierte en el informe de error.",
    failHint: "Un fallo genera un informe de error con estos pasos adjuntos.",
    bugFiled: "Informe de error creado con sus pasos y lo que vio.",
    viewBug: "Abrir el informe de error",
    retestTitle: "Vuelva a probar esto",
    retestImpact: "El código que cubre esta prueba cambió: {{path}} (a {{hops}} paso(s)).",
    retestBroad: "Se ha introducido un cambio amplio, así que se revisan todas las pruebas de usuario.",
    retestUndetermined: "No pudimos determinar si el cambio afecta a esta prueba, por eso se lo preguntamos.",
    dismissRetest: "No afectada",
    dismissExplain: "Si el cambio no pudo afectar a lo que verificó, explique por qué. Su aprobación anterior se mantiene.",
    dismissPlaceholder: "Por qué este cambio no afecta a esta prueba",
    confirmDismiss: "Descartar",
  },
  addTest: {
    title: "Añadir una prueba a partir de lo que encontró",
    intro: "Encontró algo que la especificación no previó. Anótelo para que se compruebe en cada versión.",
    whatHappened: "Qué debería ser cierto",
    titlePlaceholder: "p. ej. Cambiar de fase con un temporizador activo mantiene la duración correcta",
    setup: "Preparación (una por línea)",
    setupPlaceholder: "p. ej. Un proyecto con un temporizador en marcha",
    steps: "Pasos",
    actionPlaceholder: "Haga esto…",
    expectedPlaceholder: "Debería ver… (un valor, un recuento, un mensaje)",
    addStep: "Añadir un paso",
    after: "Comprobar después (una por línea)",
    afterPlaceholder: "p. ej. El total de tiempo registrado no cambia",
    vagueWarning: "El/los paso(s) {{steps}} dicen algo como \"funciona\". Indique qué debería verse: un valor, un recuento o un mensaje.",
    needTitle: "Ponga un título a la prueba",
    needStep: "Añada al menos un paso",
    needExpected: "Cada paso necesita un resultado esperado",
    failed: "No se pudo guardar la prueba",
    cancel: "Cancelar", save: "Guardar prueba",
  },
  qaPhase: {
    testTasks: "Tareas de prueba",
    passCount: "{{n}} aprobado",
    failCount: "{{n}} fallido",
  },
  humanTestingPhase: {
    feedbackItems: "Elementos de retroalimentación",
    issuesRaisedByTesters: "Problemas planteados por los evaluadores humanos",
    openCount: "{{count}} abierto(s)",
  },
  betaPhase: {
    approvalTitle: "Fase Beta — Se requiere aprobación del administrador",
    approvalBody: "La creación de nuevas tareas durante la fase Beta requiere la aprobación explícita del administrador. Esto se registrará en el registro de auditoría.",
    approveAndCreate: "Aprobar y crear",
    feedbackTitle: "Comentarios de Beta",
    feedbackHint: "Las modificaciones requieren la aprobación del administrador",
    openCount: "{{count}} abierto(s)",
    creatingApprovedTask: "Creando la tarea aprobada...",
  },
  releasePhase: {
    statusComplete: "Completado",
    statusInProgress: "En curso",
    statusReopened: "Reabierto",
    statusPending: "Pendiente",
    manualDeployment: "Despliegue manual",
    ciCdPipeline: "Canalización CI/CD",
    stagedRollout: "Despliegue por fases",
    hotfix: "Corrección urgente",
    releaseRecord: "Registro de versión",
    priorPhasesIncomplete: "Fases anteriores incompletas",
    versionNumber: "Número de versión",
    versionPlaceholder: "p. ej. v1.0.0",
    releaseDate: "Fecha de versión",
    releaseMethod: "Método de versión",
    releaseNotes: "Notas de versión",
    notesPlaceholder: "Resuma lo que se publicó, los cambios clave, los problemas conocidos...",
    saved: "Guardado",
    saveNotes: "Guardar notas",
    fullLifecycleSummary: "Resumen completo del ciclo de vida",
    columnPhase: "Fase",
    columnStatus: "Estado",
    columnEntered: "Iniciada",
    columnCompleted: "Completada",
    projectLabel: "Proyecto",
  },
  phaseCompletionBar: {
    advanceFailed: "No se pudo avanzar la fase",
    reopenFailed: "No se pudo reabrir la fase",
    checkingCriteria: "Comprobando los criterios de finalización...",
    phaseComplete: "Fase completada",
    phaseReopened: "Fase reabierta",
    readyToAdvance: "Listo para avanzar",
    criteriaMet: "{{pass}} de {{total}} criterios cumplidos",
    collapse: "Contraer",
    expand: "Expandir",
    advancing: "Avanzando...",
    markAsReleased: "Marcar como publicado",
    advancePhase: "Avanzar fase",
    reopenedForRevisions: "Esta fase se ha reabierto para revisiones.",
    cancelReopen: "Cancelar reapertura",
    reopenPhase: "Reabrir fase",
    reopenReasonPlaceholder: "Motivo para reabrir esta fase...",
    reopening: "Reabriendo...",
    confirmReopen: "Confirmar reapertura",
  },
  phaseTaskGroup: {
    unassigned: "Sin asignar",
    unnamedScope: "Scope sin nombre",
    noTasksInScope: "No hay tareas en este scope.",
    noTasksInPhase: "No hay tareas en esta fase.",
    unscopedTasks: "Tareas sin scope",
    failedToLoad: "Error al cargar las tareas",
    requestFailed: "La solicitud falló: {{status}}",
  },
  taskList: {
    statusOpen: "Abierto",
    statusInProgress: "En curso",
    statusCompleted: "Completado",
    statusDismissed: "Descartado",
    resultNone: "—",
    resultPass: "Aprobado",
    resultFail: "Fallido",
    resultBlocked: "Bloqueado",
    priorityLow: "Baja",
    priorityMed: "Med.",
    priorityHigh: "Alta",
    priorityCrit: "Crít.",
    taskNamePlaceholder: "Nombre de la tarea...",
    descriptionPlaceholder: "Descripción (opcional)...",
    addTaskButton: "Añadir tarea",
    addTask: "Añadir tarea",
    createError: "No se pudo crear la tarea",
    noTasks: "Aún no hay tareas",
    colTask: "Tarea",
    colResult: "Resultado",
    colRaisedBy: "Reportado por",
    colPriority: "Prioridad",
    colStatus: "Estado",
    countOpen: "{{n}} abiertas",
    countClosed: "{{n}} cerradas",
    countTotal: "{{n}} en total",
  },
  phase: {
    design: "Diseño",
    implementation: "Implementación",
    qa: "QA",
    humanTesting: "Pruebas humanas",
    beta: "Beta",
    release: "Publicación",
  },
  projectPanel: {
    saveFailed: "Error al guardar",
    failedToLoad: "Error al cargar",
    loadingTasks: "Cargando tareas…",
    noTasksForScope: "Sin tareas para este scope.",
    filterAll: "todas",
    filterOpen: "abiertas",
    filterClosed: "cerradas",
    noFilterTasks: "Sin tareas {{filter}}.",
    openTasks: "Tareas abiertas",
    noOpenTasks: "Sin tareas abiertas.",
    statTotal: "Total",
    statOpen: "Abierto",
    statClosed: "Cerrado",
    statInProgress: "En progreso",
    statComplete: "Completado",
    statActive: "Activo",
    statResolved: "Resuelto",
    statScopesPassing: "Scopes aprobados",
    statQaTasksDone: "Tareas QA hechas",
    statNoTasks: "Sin tareas",
    documents: "Documentos",
    completeCheck: "✓ completado",
    tasksCount: "Tareas ({{done}}/{{total}})",
    specOpenClosed: "Spec {{num}}: Abiertas {{open}} · Cerradas {{closed}}",
    // [SCOPE 137 / T014] Change Impact agreement log (read-only)
    changeImpactTitle: "Registro de Change Impact",
    changeImpactCount: "Registro de Change Impact ({{count}})",
    changeImpactLoading: "Cargando…",
    changeImpactEmpty: "No se han registrado acuerdos ni cruces para este scope.",
    changeImpactError: "No se pudo cargar el registro de Change Impact.",
    changeImpactKindAck: "Reconocido",
    changeImpactKindApproval: "Aprobado",
    changeImpactKindDeclined: "Rechazado",
    changeImpactKindCrossing: "Cruzado sin supervisión",
    changeImpactAffected: "{{count}} afectados",
    changeImpactQueued: "enviado tras un periodo sin conexión",
    changeImpactReadOnly: "Solo lectura. Estos registros no se pueden modificar.",
    reopen: "Reabrir",
    reactivate: "Reactivar",
    markClarified: "Marcar aclarado",
    defer: "Aplazar",
    needsClarification: "Requiere aclaración",
    deleteScope: "Eliminar",
    deleteScopeConfirm: "¿Eliminar permanentemente el scope {{num}} «{{title}}» y sus {{tasks}} tarea(s) y {{docs}} documento(s)? Esta acción no se puede deshacer.",
    deleteScopeError: "No se pudo eliminar el scope. Inténtalo de nuevo.",
    overviewDoc: "Doc de resumen",
    scopeCountOne: "{{n}} scope",
    scopeCountOther: "{{n}} scopes",
    fullyGenerated: "{{n}} totalmente generados",
    noScopesCreatedYet: "Aún no se han creado scopes.",
    scopeDocuments: "Documentos de scope",
    noScopes: "Sin scopes.",
    progressByScope: "Progreso por scope",
    qaStatusByScope: "Estado QA por scope",
    allTestsPassed: "Los {{n}} tests superados",
    inProgressDone: "{{n}} en progreso · {{done}}/{{total}} hechas",
    completeCount: "{{done}}/{{total}} completadas",
    noQaTasks: "Sin tareas QA",
    betaApprovalNotice: "Las nuevas tareas en Beta requieren la aprobación de un ADMIN antes de crearse.",
    feedbackByScope: "Comentarios por scope",
    allScopesClear: "Todos los scopes despejados — listo para publicar.",
    openItemsRemain: "Quedan {{n}} elementos abiertos en los scopes.",
    phaseChecklist: "Lista de verificación de fases",
    openItemsByScope: "Elementos abiertos por scope",
    openResolved: "{{open}} abiertos · {{done}}/{{total}} resueltos",
    phaseView: "Vista {{phase}}",
    allProjects: "Todos los proyectos",
    open: "Abrir",
    status: {
      draft: "Borrador",
      inReview: "En revisión",
      pending: "Pendiente",
      clarified: "Aclarado",
      planned: "Planificado",
      tasksGenerated: "Tareas generadas",
      implemented: "Implementado",
      qaPassed: "QA superada",
      humanTested: "Probado por humanos",
      betaCleared: "Beta aprobada",
      released: "Publicado",
      deferred: "Aplazado",
    },
    doctype: {
      tasks: "Tareas",
      datamodel: "Modelo de datos",
      quickstart: "Inicio rápido",
      checklist: "Lista de verificación",
      contracts: "Contratos",
      research: "Investigación",
      overview: "Resumen",
      stackStyle: "Stack y estilo",
    },
    issuetype: {
      bug: "Error",
      feature: "Función",
      enhancement: "Mejora",
      task: "Tarea",
    },
  },
  helpCatalog: {
    ui: {
      included: "Incluido",
      upgrade: "Mejorar",
      admin: "Admin",
      adminOnly: "Solo administrador",
      howToLabel: "Cómo:",
      howToUseIt: "Cómo usarlo",
      availableFromPrefix: "Disponible desde el plan ",
      availableFromSuffix: ".",
      yourPlan: "Tu plan",
      subscriptionPlan: "Plan de suscripción",
    },
    category: {
      coreWorkspace: "Espacio de trabajo principal",
      developmentLifecycle: "Ciclo de vida del desarrollo",
      integrations: "Integraciones",
      teamCollaboration: "Equipo y colaboración",
      timeBillingConsulting: "Tiempo, facturación y consultoría",
      adminConfiguration: "Administración y configuración",
      securityCompliance: "Seguridad y cumplimiento",
      enterpriseSupport: "Empresa y soporte",
    },
    plan: {
      SINGLE_USER: {
        label: "Usuario individual",
        userLimit: "1 usuario",
      },
      SMALL_BUSINESS: {
        label: "Pequeña/Mediana empresa",
        userLimit: "Hasta 5 usuarios",
      },
      CONSULTANT: {
        label: "Consultor",
        userLimit: "Equipo ilimitado",
      },
      ENTERPRISE: {
        label: "Empresa",
        userLimit: "25+ usuarios",
      },
    },
    feature: {
      dashboard: {
        title: "Panel de control",
        description: "Tu resumen de proyectos: el progreso de las fases del Lifecycle por proyecto, además de enlaces rápidos a los documentos generados de cada proyecto.",
        howTo: "Abre el Dashboard desde la barra lateral. Cada tarjeta de proyecto muestra su progreso por fase y sus enlaces a documentos; los ADMIN disponen de un botón Nuevo para crear un proyecto directamente.",
      },
      projects: {
        title: "Proyectos",
        description: "Crea y gestiona proyectos. Proyectos ilimitados en todos los planes de pago.",
        howTo: "Crea un proyecto con el botón Nuevo del panel o desde el menú Proyectos, y luego ábrelo para ver sus pestañas (resumen, lifecycle, tareas).",
      },
      tasks: {
        title: "Mis tareas (Kanban)",
        description: "El trabajo que tienes asignado en un tablero Kanban a través de todos los proyectos.",
        howTo: "Abre Mis tareas; arrastra las tarjetas entre columnas para cambiar su estado, o abre una tarjeta para ver los detalles.",
      },
      settings: {
        title: "Configuración",
        description: "Ajustes de cuenta, empresa y preferencias, incluido el idioma.",
        howTo: "Abre Configuración desde el menú; aquí están el selector de idioma y el perfil.",
      },
      faq: {
        title: "Preguntas frecuentes",
        description: "Respuestas a las preguntas comunes sobre el uso de wxKanban.",
        howTo: "Abre las Preguntas frecuentes desde el menú.",
      },
      lifecycle: {
        title: "Lifecycle de 6 fases y orquestador",
        description: "La metodología Design → Implementation → QA → HumanTesting → Beta → Release que controla cada funcionalidad y mantiene una única fuente de verdad sobre el estado de un proyecto.",
        howTo: "Contrólalo desde Claude Code + el kit de wxKanban en VS Code (buildscope → createSpecs → implement). El comando correcto solo está disponible en la fase correcta.",
      },
      specs: {
        title: "Specs y Scopes",
        description: "Los documentos Scope-of-project y las Specs, planes y tareas generados para cada funcionalidad.",
        howTo: "Redacta un Scope con buildscope; genera la Spec, el plan y las tareas con createSpecs; haz el seguimiento en el Cockpit.",
      },
      audit: {
        title: "Informes de auditoría",
        description: "Un registro de auditoría por release de lo que se construyó, se probó y se delimitó con marcas de código.",
        howTo: "Abre el enlace Informe de auditoría en una tarjeta de proyecto, o vuelve a generarlo desde el panel de Admin.",
      },
      cockpit: {
        title: "Dev Cockpit",
        description: "Una barra lateral de VS Code que muestra el trabajo restante por Scope, en tiempo real a medida que se construye.",
        howTo: "Instala la extensión Dev Cockpit; fija tu Scope activo y enumera las tareas pendientes.",
      },
      pmIntegrations: {
        title: "Integraciones PM",
        description: "Conecta Jira, Monday.com, Asana y Trello y sincroniza las tareas en ambos sentidos.",
        howTo: "Abre Integraciones PM y conéctate mediante OAuth; la sincronización se ejecuta según una programación en segundo plano.",
      },
      team: {
        title: "Gestión del equipo",
        description: "Invita a compañeros, asigna roles y gestiona el acceso con controles basados en roles.",
        howTo: "Abre Equipo; invita por correo electrónico y define el rol de cada miembro.",
      },
      timeTracking: {
        title: "Seguimiento de tiempo y facturación",
        description: "Temporizador integrado con seguimiento de horas facturables y registros de tiempo.",
        howTo: "Inicia el temporizador en una tarea; marca los registros como facturables para la facturación.",
      },
      invoices: {
        title: "Facturas",
        description: "Genera facturas en PDF a partir de los registros de tiempo facturables.",
        howTo: "Abre Facturas, elige un proyecto y un rango de fechas, y genera; los registros facturados se marcan para evitar duplicados.",
      },
      reports: {
        title: "Informes",
        description: "Informes avanzados sobre tiempo, proyectos y resultados.",
        howTo: "Abre Informes desde el menú.",
      },
      workHub: {
        title: "Work Hub",
        description: "Un centro de gestión de encargos para administrar el trabajo con clientes.",
        howTo: "Abre el Work Hub para ver y gestionar tus encargos.",
      },
      clientPortal: {
        title: "Portal del cliente",
        description: "Ofrece a los clientes una vista restringida y de solo lectura de su proyecto.",
        howTo: "Activa el portal para un proyecto y comparte el enlace del cliente.",
      },
      customWorkflows: {
        title: "Flujos de trabajo personalizados",
        description: "Adapta los flujos de trabajo del Lifecycle al proceso de tu equipo.",
        howTo: "Configura flujos de trabajo personalizados en la configuración del proyecto.",
      },
      apiAccess: {
        title: "Acceso a la API",
        description: "Acceso programático a tus datos de wxKanban mediante API.",
        howTo: "Crea un token de API en Configuración y llama a los endpoints documentados.",
      },
      stackProfiles: {
        title: "Perfiles de Stack",
        description: "Definiciones reutilizables de Stack objetivo y de estilo que se aplican al crear un proyecto.",
        howTo: "Abre Perfiles de Stack (Admin) para definir o editar los stacks de tu empresa.",
      },
      themeProfiles: {
        title: "Perfiles de tema",
        description: "Temas de apariencia reutilizables para los resultados generados.",
        howTo: "Abre Perfiles de tema (Admin) para definir o editar temas.",
      },
      encryption: {
        title: "Cifrado en reposo",
        description: "Los secretos se cifran en reposo con AES-256-GCM.",
        howTo: "Siempre activo: no requiere configuración.",
      },
      soc2: {
        title: "SOC 2 y HIPAA",
        description: "Controles y herramientas de cumplimiento alineados con SOC 2 y HIPAA.",
        howTo: "Agrega los documentos SDLC de tu empresa para el análisis de cumplimiento; asigna controles a los miembros del equipo.",
      },
      sso: {
        title: "Inicio de sesión único (SSO)",
        description: "SSO empresarial para tu organización.",
        howTo: "Contacta a tu representante de wxKanban para configurar tu proveedor de identidad.",
      },
      advancedSecurity: {
        title: "Seguridad avanzada",
        description: "Controles de seguridad adicionales de nivel empresarial.",
        howTo: "Se configura como parte de la incorporación Enterprise.",
      },
      emailSupport: {
        title: "Soporte por correo electrónico",
        description: "Soporte de producto por correo electrónico.",
        howTo: "Envía un correo a admin@wxperts.com.",
      },
      dedicatedSupport: {
        title: "Soporte dedicado",
        description: "Un contacto de soporte dedicado para tu cuenta.",
        howTo: "Se asigna durante la incorporación Enterprise.",
      },
      customIntegrations: {
        title: "Integraciones personalizadas",
        description: "Integraciones a medida creadas para tu organización.",
        howTo: "Define el alcance de las integraciones personalizadas con tu representante de wxKanban.",
      },
      sla: {
        title: "Garantía de SLA",
        description: "Un SLA contractual de tiempo de actividad y de tiempo de respuesta.",
        howTo: "Forma parte de tu acuerdo Enterprise.",
      },
    },
  },
  // [SCOPE 052] Páginas públicas de Ayuda/Tutoriales (/docs, /docs/:slug)
  docs: {
    title: "Ayuda/Tutoriales",
    backHome: "← Volver al inicio",
    sectionsLabel: "Secciones de ayuda",
    navFaq: "Preguntas frecuentes",
    navVideos: "Vídeos",
    navSessions: "Sesiones de formación",
    navGuides: "Guías",
    guidesHeading: "Guías",
    guidesSubtitle: "Guías prácticas descargables que puedes conservar junto a tu proyecto.",
    guidesConversionTitle: "Guía de conversión WINDEV / WEBDEV",
    guidesConversionSummary: "Cómo llevar una aplicación WINDEV o WEBDEV heredada a un stack moderno de React y PostgreSQL, paso a paso.",
    guidesDraftBadge: "Borrador",
    guidesDraftNote: "Esta guía todavía es un borrador. Los pasos son utilizables, pero la redacción y los detalles pueden cambiar.",
    guidesLoading: "Cargando guías…",
    guidesEmpty: "Todavía no hay guías disponibles.",
    guidesLanguageLabel: "Idioma",
    guidesLanguageAll: "Todos los idiomas",
    faqHeading: "Preguntas frecuentes",
    faqSubtitle: "Respuestas a las preguntas más comunes sobre wxKanban.",
    faqEmpty: "Aún no hay preguntas frecuentes disponibles.",
    sessionsHeading: "Próximas sesiones de formación en línea",
    sessionsSubtitle: "Únase a una sesión en directo con un instructor para empezar rápidamente.",
    sessionsEmpty: "No hay sesiones programadas en este momento.",
    sessionsRegister: "Muestre su interés y le avisaremos cuando se abra la próxima sesión.",
    sessionsCta: "Contáctenos para registrarse",
    sessionsJoin: "Inscribirse",
    videosHeading: "Vídeos de formación",
    videosSubtitle: "Vea tutoriales paso a paso de wxKanban.",
    videosEmpty: "Aún no hay vídeos de formación disponibles.",
    loading: "Cargando…",
    loadError: "No se pudo cargar la documentación. Inténtelo de nuevo más tarde.",
    backAll: "← Toda la documentación",
    detailError: "No se pudo cargar este documento. Inténtelo de nuevo más tarde.",
    notFoundTitle: "No encontrado",
    notFoundBody: "Este documento no existe o aún no está publicado.",
  },
  // [SCOPE 131 / T013] Suspend & cancel copy (FR-011, FR-009).
  subscriptionLifecycle: {
    sectionTitle: "Suspender o cancelar",
    sectionDesc: "Pausa la facturación durante un tiempo o cancela tu suscripción al final del periodo que ya has pagado.",
    stateActive: "Tu suscripción está activa.",
    statePaused: "La facturación está suspendida.",
    statePausedDetail: "No se emiten facturas y no se ha eliminado ningún dato. Reanúdala cuando quieras: tus proyectos y tu historial te esperan.",
    stateCancelPending: "Cancelación programada.",
    stateCancelPendingDetail: "Mantienes el acceso completo hasta el {{date}}. Puedes revertirlo en cualquier momento antes de esa fecha.",
    effectiveDateUnknown: "el final de tu periodo de facturación actual",
    pauseTitle: "Suspender la facturación",
    pauseDesc: "Stripe deja de facturarte y tu equipo pierde el acceso hasta que la reanudes. No se elimina nada ni se programa ninguna eliminación.",
    pauseAction: "Suspender la facturación",
    unpauseAction: "Reanudar la facturación",
    cancelTitle: "Cancelar la suscripción",
    cancelDesc: "Tu suscripción termina cuando se agota el periodo que ya has pagado. Hasta entonces conservas el acceso completo.",
    cancelAction: "Cancelar la suscripción",
    undoCancelAction: "Mantener mi suscripción",
    pauseSuccess: "Facturación suspendida. Reanúdala cuando quieras.",
    unpauseSuccess: "Facturación reanudada: bienvenido de nuevo.",
    cancelSuccess: "Tu suscripción terminará el {{date}}. Puedes revertirlo hasta entonces.",
    undoCancelSuccess: "Cancelación revertida. Tu suscripción continúa con normalidad.",
    actionFailed: "No se ha podido completar y no se ha cambiado nada. Inténtalo de nuevo.",
    dialogTitle: "¿Cancelar tu suscripción?",
    dialogBody: "Tu suscripción seguirá activa hasta el {{date}}. Después tu equipo perderá el acceso y, 30 días más tarde, los datos de tu empresa se archivarán.",
    dialogUndoNote: "Puedes revertirlo en cualquier momento antes del {{date}}.",
    dialogTypeLabel: "Escribe {{name}} para confirmar",
    dialogPlaceholder: "Nombre de la empresa",
    dialogConfirm: "Cancelar la suscripción",
    dialogKeep: "Mantener mi suscripción",
    dialogMismatch: "Eso no coincide con el nombre de tu empresa.",
    wallPausedTitle: "La facturación está suspendida",
    wallPausedBody: "Tu empresa suspendió su propia facturación, así que el producto está en pausa. No se ha eliminado nada. Un administrador puede reanudarla desde Ajustes → Facturación y suscripción.",
    wallPausedCta: "Ir a los ajustes de facturación",
    wallPausedNonAdmin: "Tu empresa suspendió su facturación. No se ha eliminado nada: pide a tu administrador que la reanude.",
    emailSubject: "Tu suscripción a wxKanban terminará el {{date}}",
    emailHeading: "Tu cancelación está confirmada",
    emailBody: "Hemos programado el fin de tu suscripción a wxKanban para el {{date}}. Mantienes el acceso completo a {{company}} hasta esa fecha y no se te volverá a cobrar.",
    emailUndoHeading: "¿Has cambiado de opinión?",
    emailUndoBody: "Puedes revertirlo en cualquier momento antes del {{date}} desde Ajustes → Facturación y suscripción. No se elimina nada mientras la cancelación siga pendiente.",
    emailFooter: "Si no has solicitado esto, ponte en contacto con el administrador de tu empresa de inmediato.",
  },
  // [SCOPE 005 / T019] Time & Billing (SPEC-005). "Resource", never "AI" (constraint 5).
  timeBilling: {
    common: {
      cancel: "Cancelar",
      close: "Cerrar",
      delete: "Eliminar",
      edit: "Editar",
      loading: "Cargando…",
      save: "Guardar",
      saving: "Guardando…",
    },
    page: {
      title: "Tiempo y facturación",
      open: "Abrir Tiempo y facturación",
      manualOnly: "El tiempo de Recursos procedente de las ejecuciones de agentes aún no está disponible. Introduce el tiempo de Recursos manualmente.",
      engagedManual: "En los proyectos de cliente en los que participas, el tiempo de Recursos se registra solo de forma manual.",
      generateStatement: "Generar extracto",
      billedTotal: "Proyectos facturados en este intervalo: {{hours}} h",
      actionFailed: "No se pudo completar la acción. Inténtalo de nuevo.",
      loadFailed: "No se pudieron cargar tus proyectos.",
    },
    range: {
      from: "Desde",
      to: "Hasta",
      thisWeek: "Esta semana",
      includeInvoiced: "Incluir el tiempo ya facturado",
      invalid: "La fecha de inicio debe ser anterior o igual a la fecha de fin.",
    },
    projects: {
      none: "Aún no hay proyectos.",
      clientProject: "Cliente: {{client}}",
      hours: "{{hours}} h",
      billThis: "Facturable",
      entries: "Registros",
      rates: "Tarifas",
      client: "Cliente",
      noClient: "— Sin cliente —",
      billedTo: "Facturado a {{client}}",
      assignClient: "Asigna un cliente para facturar este proyecto",
    },
    lines: {
      line: "Línea",
      scope: "Scope",
      developer: "Desarrollador",
      resource: "Recurso",
      developerHours: "h Desarrollador",
      resourceHours: "h Recurso",
      actions: "Acciones",
      addDeveloper: "Añadir tiempo de Desarrollador",
      addResource: "Añadir tiempo de Recurso",
      noScopes: "Este proyecto aún no tiene scopes.",
      deletedScope: "Scope eliminado",
      archived: "archivado",
      brainstorming: "Brainstorming",
      processes: "Procesos",
      dba: "DBA",
      management: "Gestión",
      meetings: "Reuniones",
      projectTotal: "Total del proyecto",
    },
    entry: {
      addDeveloper: "Añadir tiempo de Desarrollador",
      addResource: "Añadir tiempo de Recurso",
      editTitle: "Editar registro de tiempo",
      person: "Quién hizo el trabajo",
      model: "Modelo",
      date: "Fecha",
      time: "Tiempo",
      timeHelp: "Introduce 1:38, 1.5 o 45m. Un signo menos al principio indica una corrección.",
      savedAs: "Guardado como {{hours}} h (redondeado al siguiente cuarto de hora).",
      notes: "Notas",
      correctionNeedsNote: "Una corrección (tiempo negativo) necesita una nota.",
      saveFailed: "No se pudo guardar el registro.",
    },
    entries: {
      title: "Registros de tiempo — {{project}}",
      none: "No hay registros en este intervalo.",
      who: "Quién / modelo",
      hours: "h",
      invoiced: "Facturado",
      invoicedHelp: "Este registro figura en un extracto y ya no se puede modificar. Añade una corrección en un periodo posterior.",
      confirmDelete: "¿Eliminar este registro?",
      deleteFailed: "No se pudo eliminar el registro.",
      loadFailed: "No se pudieron cargar los registros.",
    },
    timer: {
      title: "Temporizador",
      idle: "No hay ningún temporizador en marcha. Pulsa el reloj de un proyecto, o ▶ en una línea, para iniciar uno.",
      startTitle: "Iniciar temporizador",
      chooseLine: "Línea",
      startHelp: "Cada inicio y parada guarda su propia entrada, así puedes registrar varios intervalos en un mismo día.",
      runningHere: "El temporizador está en marcha en este proyecto.",
      start: "Iniciar temporizador",
      stop: "Detener y guardar",
      alreadyRunning: "Ya hay un temporizador en marcha.",
      willSave: "Se guardará como {{hours}} h.",
      autoNotes: "Ejecuciones de agentes durante este temporizador",
      autoUnavailable: "Las ejecuciones de agentes aún no se registran.",
      noRuns: "Aún no hay ejecuciones de agentes.",
      refresh: "Actualizar ejecuciones de agentes",
      manualNotes: "Tus notas",
      manualPlaceholder: "p. ej. llamada de 15 minutos con el cliente sobre el scope de la auditoría",
      notesFailed: "No se pudieron cargar las ejecuciones de agentes.",
      stopFailed: "No se pudo detener el temporizador.",
      pause: "Pausar",
      resume: "Reanudar",
      paused: "En pausa: este tiempo no se factura.",
      pauseFailed: "No se pudo pausar ni reanudar el temporizador.",
    },
    statement: {
      title: "Generar extracto",
      description: "Crea un extracto para un cliente y un periodo de facturación.",
      customer: "Cliente",
      noBilledProjects: "Este cliente aún no tiene proyectos facturados",
      cycle: "Ciclo de facturación",
      cycleWeekly: "Semanal",
      cycleBiweekly: "Cada dos semanas",
      periodStart: "Inicio del periodo",
      periodEnd: "Fin del periodo",
      periodNote: "Solo se incluye el tiempo facturado y no bloqueado de este periodo. Los registros generados pasan a ser de solo lectura.",
      generate: "Generar extracto",
      working: "Procesando…",
      created: "Extracto {{name}} creado",
      number: "Número de extracto",
      total: "Total",
      entries: "Registros",
      downloadPdf: "Descargar PDF",
      periodFailed: "No se pudo cargar el periodo sugerido",
      cycleFailed: "No se pudo cambiar el ciclo de facturación",
      generateFailed: "No se pudo generar el extracto",
      downloadFailed: "No se pudo descargar el PDF",
    },
    rates: {
      title: "Tarifas del proyecto",
      unchanged: "Déjalo en blanco para mantener",
      useDefault: "Usar la tarifa predeterminada de la empresa",
      help: "Los registros nuevos usan la tarifa vigente. Los registros existentes conservan la tarifa con la que se crearon.",
      companyDefault: "tarifa predeterminada de la empresa",
      saved: "Guardado. Desarrollador: {{developer}} · Recurso: {{resource}}",
      saveFailed: "No se pudieron guardar las tarifas.",
    },
    settings: {
      title: "Tiempo y facturación",
      description: "Tarifas por hora predeterminadas y datos bancarios impresos en los extractos de clientes.",
      developerRate: "Tarifa de Desarrollador ($/h)",
      resourceRate: "Tarifa de Recurso ($/h)",
      ratesHelp: "Un proyecto puede sustituir cualquiera de las dos tarifas. Cambiar una tarifa no modifica los registros existentes.",
      achTitle: "Datos de pago ACH",
      achComplete: "Completo",
      achIncomplete: "Incompleto — los extractos indicarán que ACH no está configurado",
      bankName: "Nombre del banco",
      accountName: "Nombre de la cuenta",
      routingNumber: "Número de ruta",
      accountNumber: "Número de cuenta",
      endingIn: "terminada en {{last4}}",
      notSet: "sin definir",
      replacePlaceholder: "Escribe un número nuevo para sustituirlo",
      achHelp: "Almacenado cifrado. Aquí solo se muestran los últimos 4 dígitos; los números completos solo aparecen en los extractos.",
      nothingChanged: "No hay nada que guardar.",
      saved: "Guardado.",
      saveFailed: "No se pudo guardar la configuración.",
      loadFailed: "No se pudo cargar la configuración.",
    },
  },
};
